import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  clearBackgroundCreateJob,
  formatCreateJobElapsed,
  getBackgroundCreateJobs,
  requestBackgroundCreateExpand,
  subscribeBackgroundCreateJobs,
} from '../lib/backgroundCreateJob';

function jobTitle(kind) {
  return kind === 'training' ? 'Training plan' : 'Meal plan';
}

function BannerRow({ kind, job, elapsedLabel, onShowProgress }) {
  if (!job || job.progressVisible) return null;
  const title = jobTitle(kind);
  const href = kind === 'training' && job.planId
    ? `/training-plans/${job.planId}`
    : '/nutrition';

  return (
    <div className="pointer-events-auto flex flex-wrap items-center gap-3 rounded-xl bg-[var(--bg-card)] px-4 py-3 shadow-[var(--shadow-panel)]">
      <p className="text-sm text-[var(--text-primary)] min-w-0 flex-1">
        <span className="font-semibold">{title}. </span>
        {job.status === 'running'
          ? 'Still creating.'
          : job.status === 'ready'
            ? (job.message || 'Ready.')
            : (job.message || 'Could not finish. Try again.')}
        {job.status === 'running' && elapsedLabel ? ` ${elapsedLabel}` : ''}
      </p>
      {job.status === 'running' ? (
        <button
          type="button"
          className="text-sm font-semibold text-primary-600 dark:text-primary-400 min-h-[44px] inline-flex items-center"
          onClick={() => onShowProgress(kind)}
        >
          Show progress
        </button>
      ) : null}
      {job.status === 'ready' ? (
        <Link to={href} className="text-sm font-semibold text-primary-600 dark:text-primary-400 min-h-[44px] inline-flex items-center">
          View
        </Link>
      ) : null}
      {job.status !== 'running' ? (
        <button
          type="button"
          className="text-sm font-semibold text-[var(--text-secondary)] min-h-[44px] min-w-[44px]"
          onClick={() => clearBackgroundCreateJob(kind)}
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );
}

export default function BackgroundCreateBanner() {
  const [snapshot, setSnapshot] = useState(getBackgroundCreateJobs);
  const [now, setNow] = useState(() => Date.now());
  const [sheetKind, setSheetKind] = useState(null);

  useEffect(() => subscribeBackgroundCreateJobs(setSnapshot), []);

  const trainingVisible = snapshot.training && !snapshot.training.progressVisible;
  const mealVisible = snapshot.meal && !snapshot.meal.progressVisible;
  const sheetJob = sheetKind ? snapshot[sheetKind] : null;
  const clockNeeded = Boolean(
    (trainingVisible && snapshot.training.status === 'running')
    || (mealVisible && snapshot.meal.status === 'running')
    || (sheetJob && sheetJob.status === 'running')
  );

  useEffect(() => {
    if (!clockNeeded) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [clockNeeded]);

  useEffect(() => {
    if (sheetKind && !snapshot[sheetKind]) setSheetKind(null);
  }, [sheetKind, snapshot]);

  const showProgress = (kind) => {
    const handled = requestBackgroundCreateExpand(kind);
    if (!handled) setSheetKind(kind);
  };

  if (!trainingVisible && !mealVisible && !sheetJob) return null;

  const sheet = sheetJob ? (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={`${jobTitle(sheetKind)} progress`}>
      <div className="modal-content bg-[var(--bg-card)] rounded-2xl p-5 max-w-lg shadow-2xl">
        <h2 className="text-xl font-bold text-[var(--text-primary)]">{jobTitle(sheetKind)}</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-2">
          {sheetJob.status === 'running'
            ? 'Still creating.'
            : (sheetJob.message || 'Could not finish. Try again.')}
        </p>
        {sheetJob.status === 'running' ? (
          <p className="text-sm text-[var(--text-muted)] mt-2">
            {formatCreateJobElapsed(sheetJob.startedAt, now)}
          </p>
        ) : null}
        <button
          type="button"
          className="mt-4 min-h-[44px] min-w-[44px] text-sm font-semibold text-[var(--text-secondary)]"
          aria-label="Close progress"
          onClick={() => setSheetKind(null)}
        >
          Close
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {trainingVisible || mealVisible ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
          <div className="flex w-full max-w-lg flex-col gap-2">
            <BannerRow
              kind="training"
              job={snapshot.training}
              elapsedLabel={formatCreateJobElapsed(snapshot.training?.startedAt, now)}
              onShowProgress={showProgress}
            />
            <BannerRow
              kind="meal"
              job={snapshot.meal}
              elapsedLabel={formatCreateJobElapsed(snapshot.meal?.startedAt, now)}
              onShowProgress={showProgress}
            />
          </div>
        </div>
      ) : null}
      {sheet ? createPortal(sheet, document.body) : null}
    </>
  );
}
